/**
 * AuthCallbackPage – v4 (Self-Contained OAuth Handler)
 *
 * Xử lý redirect từ Google OAuth (và các OAuth providers khác).
 *
 * TẠI SAO CẦN FILE NÀY:
 * ─────────────────────
 * supabase.ts v7 tắt detectSessionInUrl=false để tránh Supabase parse URL
 * trên mọi trang (gây delay/treo). Thay vào đó, callback page tự xử lý:
 *
 * FLOW:
 * ─────
 * 1. URL có code= (PKCE flow) → gọi exchangeCodeForSession()
 *    → Supabase exchange code → lấy session → fire SIGNED_IN event
 *    → AuthContext handleAuthUser → fetchProfile → setProfile
 *
 * 2. URL có access_token trong hash (implicit flow – legacy)
 *    → gọi setSession() với token từ hash
 *    → fire SIGNED_IN event → AuthContext xử lý
 *
 * 3. URL có error= → hiện lỗi rõ ràng cho user
 *
 * 4. Sau khi AuthContext xử lý xong (isAuthenticated + profile) → navigate
 *
 * KHÔNG có:
 * - Phụ thuộc vào detectSessionInUrl (đã tắt)
 * - Timeout riêng (AuthContext safety timer 5s đã đủ)
 * - onAuthStateChange listener riêng (dùng AuthContext)
 *
 * Fallback: nếu sau 15s vẫn không navigate → về login với lỗi
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/core/auth/AuthContext';
import { supabase } from '@/core/database/supabase';
import { brandConfig } from '@/config/brand.config';

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const { isAuthenticated, profile, isLoading, authError, getRedirectPath } = useAuth();
  const navigated = useRef(false);
  const exchanged = useRef(false);
  const [callbackError, setCallbackError] = useState<string | null>(null);
  const [status, setStatus] = useState<'processing' | 'error'>('processing');

  // ── Step 1: Parse URL và exchange code/token ─────────────
  useEffect(() => {
    if (exchanged.current) return;
    exchanged.current = true;

    const url = new URL(window.location.href);
    const hash = window.location.hash;

    // Trường hợp 1: URL có error (OAuth provider trả về lỗi)
    const errorParam = url.searchParams.get('error');
    const errorDesc = url.searchParams.get('error_description');
    if (errorParam) {
      const msg = errorDesc
        ? decodeURIComponent(errorDesc.replace(/\+/g, ' '))
        : `OAuth error: ${errorParam}`;
      console.error('[AuthCallback] OAuth error from provider:', msg);
      setCallbackError(msg);
      setStatus('error');
      return;
    }

    // Trường hợp 2: PKCE flow – URL có code=
    const code = url.searchParams.get('code');
    if (code) {
      console.debug('[AuthCallback] Exchanging PKCE code for session');
      supabase.auth.exchangeCodeForSession(window.location.href)
        .then(({ error }) => {
          if (error) {
            console.error('[AuthCallback] exchangeCodeForSession error:', error.message);
            setCallbackError(`Không thể xác thực: ${error.message}`);
            setStatus('error');
          }
          // Nếu thành công → SIGNED_IN event fire → AuthContext xử lý
          // Clean URL để không re-exchange nếu user refresh
          window.history.replaceState({}, '', window.location.pathname);
        })
        .catch((err) => {
          console.error('[AuthCallback] exchangeCodeForSession exception:', err);
          setCallbackError('Lỗi xác thực OAuth. Vui lòng thử lại.');
          setStatus('error');
        });
      return;
    }

    // Trường hợp 3: Implicit flow – hash có access_token
    if (hash.includes('access_token')) {
      console.debug('[AuthCallback] Parsing implicit flow tokens from hash');
      const params = new URLSearchParams(hash.substring(1));
      const accessToken = params.get('access_token');
      const refreshToken = params.get('refresh_token');

      if (accessToken && refreshToken) {
        supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken })
          .then(({ error }) => {
            if (error) {
              console.error('[AuthCallback] setSession error:', error.message);
              setCallbackError(`Không thể thiết lập phiên: ${error.message}`);
              setStatus('error');
            }
            // Clean hash
            window.history.replaceState({}, '', window.location.pathname);
          })
          .catch((err) => {
            console.error('[AuthCallback] setSession exception:', err);
            setCallbackError('Lỗi thiết lập phiên đăng nhập.');
            setStatus('error');
          });
      } else {
        setCallbackError('Token OAuth không hợp lệ.');
        setStatus('error');
      }
      return;
    }

    // Trường hợp 4: Không có code hoặc token → có thể đã được xử lý
    // Chờ AuthContext xử lý (INITIAL_SESSION đã có session từ localStorage)
    console.debug('[AuthCallback] No code/token in URL – waiting for AuthContext');
  }, []);  

  // ── Step 2: Navigate khi auth xong ──────────────────────
  useEffect(() => {
    if (navigated.current) return;

    // Có lỗi callback → về login với message
    if (callbackError) {
      navigated.current = true;
      navigate('/auth/login', { replace: true, state: { error: callbackError } });
      return;
    }

    // authError từ AuthContext (tài khoản bị khóa, profile fail)
    if (authError) {
      navigated.current = true;
      navigate('/auth/login', { replace: true, state: { error: authError } });
      return;
    }

    // Đang xử lý → chờ
    if (isLoading) return;

    // Auth xong + profile ready → navigate theo role
    if (isAuthenticated && profile) {
      navigated.current = true;
      const dest = getRedirectPath(profile);
      console.debug('[AuthCallback] Auth complete, navigating to:', dest);
      navigate(dest, { replace: true });
      return;
    }

    // Auth xong nhưng không authenticated → về login
    if (!isLoading && !isAuthenticated) {
      navigated.current = true;
      navigate('/auth/login', { replace: true });
    }
  }, [isAuthenticated, profile, isLoading, authError, callbackError, navigate, getRedirectPath]);

  // ── Fallback timeout 15s ─────────────────────────────────
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!navigated.current) {
        console.error('[AuthCallback] Fallback timeout – forcing redirect to login');
        navigated.current = true;
        navigate('/auth/login', {
          replace: true,
          state: { error: 'Xác thực mất quá nhiều thời gian. Vui lòng thử lại.' },
        });
      }
    }, 15_000);
    return () => clearTimeout(timer);
  }, [navigate]);

  // ── Render ───────────────────────────────────────────────
  if (status === 'error' && callbackError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 gap-6 p-6">
        <div className="w-16 h-16 flex items-center justify-center bg-red-100 rounded-2xl">
          <i className="ri-error-warning-line text-red-600 text-3xl" />
        </div>
        <div className="text-center max-w-sm">
          <p className="text-base font-semibold text-slate-800 mb-2">Đăng nhập thất bại</p>
          <p className="text-sm text-slate-500">{callbackError}</p>
        </div>
        <button
          onClick={() => navigate('/auth/login', { replace: true })}
          className="px-6 py-2.5 bg-green-600 text-white text-sm font-medium rounded-xl hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap"
        >
          Quay lại đăng nhập
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 gap-6">
      <div className="relative">
        <div className="w-16 h-16 flex items-center justify-center bg-green-600 rounded-2xl">
          <i className={`${brandConfig.logo.icon} text-white text-3xl`} />
        </div>
        <div className="absolute -bottom-1 -right-1 w-6 h-6 flex items-center justify-center bg-white rounded-full border-2 border-green-100">
          <i className="ri-loader-4-line animate-spin text-green-600 text-xs" />
        </div>
      </div>
      <div className="text-center">
        <p className="text-base font-semibold text-slate-800">{brandConfig.name}</p>
        <p className="text-sm text-slate-500 mt-1">Đang hoàn tất đăng nhập...</p>
      </div>
    </div>
  );
}
